import styled from '@emotion/styled';
import {
  List,
  ListItem as _ListItem,
  Table as _Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import ProductPricing from '../../../../entity/ProductPricing';
import { CreativeFormat, Option } from '../../../../generated/graphql';
import theme from '../../../../theme';
import { calculateOptionMerchandisePoint } from '../../../../utility/calculatePoint';
import { SPACING_UNIT } from '../../../constant/spacing';
import { InputtedCreationSet } from '../../../types/InputtedCreationSet';
import { InputtedCreativeFormat } from '../../../types/InputtedCreativeFormat';
import CreativeFormatCreativeTypeIcon from '../CreativeFormatCreativeTypeIcon';
import _NetworkIcon from '../CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from '../CreativeFormatPlacementLabel';

type Props = {
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  pricingMerchandiseOptions: ReadonlyArray<Option>;
  creationSet: InputtedCreationSet;
  className?: string;
};

const OptionsList: FC<Props> = ({
  isPointRequired,
  productPricing,
  pricingMerchandiseOptions,
  creationSet,
  className,
}) => {
  const { creativeFormats } = creationSet;
  const emptyCell = <DenserTableCell>-</DenserTableCell>;
  const sortedCreativeFormats: Array<InputtedCreativeFormat> = [];
  const resizeCreativeFormats = creativeFormats.filter((creativeFormat) => !!creativeFormat.resizeBaseId);

  creativeFormats.map((creativeFormat) => {
    if (!creativeFormat.resizeBaseId) {
      sortedCreativeFormats.push(creativeFormat);

      resizeCreativeFormats.map((resizeCreativeFormat) => {
        if (resizeCreativeFormat.resizeBaseId === creativeFormat.id) {
          sortedCreativeFormats.push(resizeCreativeFormat);
        }
      });
    }
  });
  const isPC = useMediaQuery(`(min-width:${theme.breakpoints.values['md']}px)`);

  return (
    <Root className={className}>
      {pricingMerchandiseOptions.length > 0 ? (
        pricingMerchandiseOptions.map((pricingMerchandiseOption) => {
          return (
            <Fragment key={`pricing-merchandise-option-${pricingMerchandiseOption.id}`}>
              <OptionName variant="h6">
                {pricingMerchandiseOption.name}
                {pricingMerchandiseOption.helpUrlForDirector ? (
                  <HelpAnchor
                    href={pricingMerchandiseOption.helpUrlForDirector}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <HelpIcon />
                  </HelpAnchor>
                ) : null}
              </OptionName>
              {isPC ? (
                <Table>
                  <TableBody>
                    {sortedCreativeFormats.map((creativeFormat) => {
                      const { network, placement, attribute, creativeType } = creativeFormat;

                      const formatOptionPoint = calculateOptionMerchandisePoint(
                        (creativeFormat as unknown) as CreativeFormat,
                        creationSet ? creationSet.expressDueDays || 0 : 0,
                        [pricingMerchandiseOption],
                      );

                      const creativeTypeCell = (
                        <DenserTableCell>
                          {!creativeFormat.resizeBaseId ? (
                            <CreativeFormatCreativeTypeIcon creativeType={creativeType} />
                          ) : null}
                        </DenserTableCell>
                      );

                      const networkCell = (
                        <DenserTableCell>
                          <NetworkIcon network={network} />{' '}
                        </DenserTableCell>
                      );

                      const placementCell = (
                        <DenserTableCell>
                          <Placement>
                            <CreativeFormatPlacementLabel network={network} placement={placement} />
                          </Placement>
                        </DenserTableCell>
                      );

                      const pointCell =
                        isPointRequired && productPricing ? (
                          <DenserTableCell>
                            <AlignRight>
                              <RequiredPoint>
                                {productPricing.currency}
                                {(formatOptionPoint * productPricing.flatRate).toLocaleString()}
                              </RequiredPoint>
                            </AlignRight>
                          </DenserTableCell>
                        ) : null;

                      const TableBodyRow = creativeFormat.resizeBaseId ? TableRow : TableRowBg;

                      if (creativeType.key === 'video') {
                        return (
                          <TableBodyRow key={`creation_set_table_row_${creativeFormat.id}`}>
                            {creativeTypeCell}
                            {networkCell}
                            {placementCell}
                            <DenserTableCell>
                              <Size>
                                {attribute.aspectRatio &&
                                attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height >
                                  0 ? (
                                  `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                                ) : (
                                  <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                                )}
                              </Size>
                            </DenserTableCell>
                            <DenserTableCell>
                              <Length>
                                {attribute.playbackTime ? (
                                  <FormattedMessage
                                    id="creative_format.playback_time"
                                    values={{ seconds: attribute.playbackTime.seconds }}
                                  />
                                ) : (
                                  '-'
                                )}
                              </Length>
                            </DenserTableCell>
                            {pointCell}
                          </TableBodyRow>
                        );
                      } else if (creativeType.key === 'static_image') {
                        return (
                          <TableBodyRow key={creativeFormat.id}>
                            {creativeTypeCell}
                            {networkCell}
                            {placementCell}
                            <DenserTableCell>
                              <Size>
                                {attribute.aspectRatio &&
                                attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height >
                                  0 ? (
                                  `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                                ) : (
                                  <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                                )}
                              </Size>
                            </DenserTableCell>
                            {emptyCell}
                            {pointCell}
                          </TableBodyRow>
                        );
                      } else {
                        return (
                          <TableBodyRow key={creativeFormat.id}>
                            {creativeTypeCell}
                            {networkCell}
                            {placementCell}
                            {emptyCell}
                            {emptyCell}
                            {emptyCell}
                            {pointCell}
                          </TableBodyRow>
                        );
                      }
                    })}
                  </TableBody>
                </Table>
              ) : (
                <List>
                  {sortedCreativeFormats.map((creativeFormat) => {
                    const { network, placement, attribute, creativeType } = creativeFormat;

                    const formatOptionPoint = calculateOptionMerchandisePoint(
                      (creativeFormat as unknown) as CreativeFormat,
                      creationSet ? creationSet.expressDueDays || 0 : 0,
                      [pricingMerchandiseOption],
                    );

                    const creativeTypeCell = (
                      <DenserTableCellCreativeIcon>
                        {!creativeFormat.resizeBaseId ? (
                          <CreativeFormatCreativeTypeIcon creativeType={creativeType} />
                        ) : null}
                      </DenserTableCellCreativeIcon>
                    );

                    const networkCell = (
                      <DenserTableCell>
                        <NetworkIcon network={network} />{' '}
                      </DenserTableCell>
                    );

                    const placementCell = (
                      <DenserTableCell>
                        <Placement>
                          <CreativeFormatPlacementLabel network={network} placement={placement} />
                        </Placement>
                      </DenserTableCell>
                    );

                    const pointCell =
                      isPointRequired && productPricing ? (
                        <DenserTableCell>
                          <AlignRight>
                            <RequiredPoint>
                              {productPricing.currency}
                              {(formatOptionPoint * productPricing.flatRate).toLocaleString()}
                            </RequiredPoint>
                          </AlignRight>
                        </DenserTableCell>
                      ) : null;

                    const ListRow = creativeFormat.resizeBaseId ? ListItem : ListItemBg;

                    if (creativeType.key === 'video') {
                      return (
                        <ListRow button key={`creation_set_table_row_${creativeFormat.id}`}>
                          <TableRowItem>
                            {creativeTypeCell}
                            {networkCell}
                            {placementCell}
                          </TableRowItem>
                          <TableRowItem>
                            <DenserTableCell>
                              <Size>
                                {attribute.aspectRatio &&
                                attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height >
                                  0 ? (
                                  `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                                ) : (
                                  <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                                )}
                              </Size>
                            </DenserTableCell>
                            <DenserTableCell>
                              <Length>
                                {attribute.playbackTime ? (
                                  <FormattedMessage
                                    id="creative_format.playback_time"
                                    values={{ seconds: attribute.playbackTime.seconds }}
                                  />
                                ) : (
                                  '-'
                                )}
                              </Length>
                            </DenserTableCell>
                            {pointCell}
                          </TableRowItem>
                        </ListRow>
                      );
                    } else if (creativeType.key === 'static_image') {
                      return (
                        <ListRow button key={creativeFormat.id}>
                          <TableRowItem>
                            {creativeTypeCell}
                            {networkCell}
                            {placementCell}
                          </TableRowItem>
                          <TableRowItem>
                            <DenserTableCell>
                              <Size>
                                {attribute.aspectRatio &&
                                attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height >
                                  0 ? (
                                  `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                                ) : (
                                  <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                                )}
                              </Size>
                            </DenserTableCell>
                            {pointCell}
                          </TableRowItem>
                        </ListRow>
                      );
                    } else {
                      return (
                        <ListRow button key={creativeFormat.id}>
                          <TableRowItem>
                            {creativeTypeCell}
                            {networkCell}
                            {placementCell}
                            {pointCell}
                          </TableRowItem>
                        </ListRow>
                      );
                    }
                  })}
                </List>
              )}
            </Fragment>
          );
        })
      ) : (
        <AlignRight>オプション無し</AlignRight>
      )}
    </Root>
  );
};

const Root = styled('div')``;

const DenserTableCell = styled(TableCell)`
  ${theme.breakpoints.down('md')} {
    padding: 16px 10px;
  }
  ${theme.breakpoints.down('sm')} {
    padding: 0 5px;
    border-bottom: none;
  }
`;
DenserTableCell.defaultProps = { padding: 'default' };

const OptionName = styled(Typography)`
  margin-top: ${SPACING_UNIT}px;
  margin-bottom: ${SPACING_UNIT}px;
`;

const HelpAnchor = styled('a')`
  display: inline-flex;
  align-items: center;
  vertical-align: -0.2rem;
  margin-left: ${SPACING_UNIT}px;
  color: ${theme.palette.secondary.main};
  text-decoration: none;
`;

const HelpIcon = styled(Help)`
  margin-right: 2px;
  font-size: 20px;
`;
const NetworkIcon = styled(_NetworkIcon)`
  height: 24px;
  width: 24px;
`;

const Placement = styled(Typography)`
  width: 140px;
  word-break: break-word;
`;
const Size = styled(Typography)``;
const Length = styled(Typography)``;

const AlignRight = styled('div')`
  text-align: right;
`;

const Table = styled(_Table)`
  margin-bottom: ${SPACING_UNIT}px;
`;

const TableRowBg = styled(TableRow)`
  background-color: #f2f6fa;
`;

const RequiredPoint = styled(Typography)``;
RequiredPoint.defaultProps = { variant: 'body1' };

const RequiredPointUnit = styled(Typography)`
  font-size: 14px;
  display: inline-block;
  font-weight: 700;
  margin-left: 2px;
  margin-right: 2px;
`;
RequiredPointUnit.defaultProps = { color: 'textSecondary' };

const ListItem = styled(_ListItem)`
  flex-direction: column;
  align-items: stretch;
  border-bottom: 1px solid rgba(224, 224, 224, 1);
`;

const ListItemBg = styled(ListItem)`
  background-color: #f2f6fa;
`;

const TableRowItem = styled(TableRow)`
  &:last-of-type {
    display: flex;
    justify-content: space-between;
  }
`;

const DenserTableCellCreativeIcon = styled(DenserTableCell)`
  min-width: 35px;
  box-sizing: border-box;
`;

export default OptionsList;
